import React, { CSSProperties } from 'react'
import styled from 'styled-components'
import { Row } from 'components/common/layout'
import { FiCheck } from 'react-icons/fi'
import { TbInfoCircle } from 'react-icons/tb'
import { theme } from 'infra/theme'
import { H5Text } from 'components/common/text'
import { AiOutlineClose } from 'react-icons/ai'
import { textEllipsis } from 'components/common/styles'
import { RadioIcon } from './icon'
import Tippy from '@tippyjs/react'
import { Balloon, BalloonType } from 'components/common/balloon'

export enum ChipType {
  FILLED = 'filled',
  FILLED_PRIMARY = 'filled-primary',
  OUTLINED = 'outlined',
  OUTLINED_LOW = 'outlined-low',
  SUPER_ROW = 'super-row',
}

export enum ChipSize {
  XX_SMALL = 'extra-extra-small',
  EXTRA_SMALL = 'extra-small',
  SMALL = 'small',
  DEFAULT = 'default',
}

const ChipTypeMapping = {
  [ChipType.FILLED]: {
    border: theme.color.black1,
    background: theme.color.primary3,
    color: theme.color.black1,
    hover: `linear-gradient(#80859D14, #80859D14),${theme.color.primary3}`,
    active: `linear-gradient(#80859D1F, #80859D1F),${theme.color.primary3}`,
  },
  [ChipType.FILLED_PRIMARY]: {
    border: theme.color.primary,
    background: theme.color.primary,
    color: theme.color.white1,
    hover: `linear-gradient(#FFFFFF1A, #FFFFFF1A), ${theme.color.primary}`,
    active: `linear-gradient(#FFFFFF38, #FFFFFF38), ${theme.color.primary}`,
  },
  [ChipType.OUTLINED]: {
    border: theme.color.primary,
    background: theme.color.white1,
    color: theme.color.primary,
    hover: `linear-gradient(#24D59E0A, #24D59E0A), ${theme.color.white1}`,
    active: `linear-gradient(#24D59E14, #24D59E14), ${theme.color.white1}`,
  },
  [ChipType.OUTLINED_LOW]: {
    border: theme.color.primary,
    background: theme.color.white1,
    color: theme.color.black4,
    hover: `linear-gradient(#24D59E0A, #24D59E0A), ${theme.color.white1}`,
    active: `linear-gradient(#24D59E14, #24D59E14), ${theme.color.white1}`,
  },
  [ChipType.SUPER_ROW]: {
    border: '#F0F0F0',
    background: '#F0F0F0',
    color: theme.color.black3,
    hover: `linear-gradient(#AAAAAA1C, #AAAAAA1C), #F0F0F0`,
    active: `linear-gradient(#AAAAAA33, #AAAAAA33), #F0F0F0`,
  },
}

const ChipSizeMapping = {
  [ChipSize.XX_SMALL]: {
    padding: '1px 8px',
    fontSize: '11px',
    borderRadius: '12px',
  },
  [ChipSize.EXTRA_SMALL]: {
    padding: '4px 8px',
    fontSize: '13px',
    borderRadius: '12px',
  },
  [ChipSize.SMALL]: {
    padding: '6px 12px',
    fontSize: '15px',
    borderRadius: '16px',
  },
  [ChipSize.DEFAULT]: {
    padding: '8px 16px',
    fontSize: '18px',
    borderRadius: '16px',
  },
}

const mapMobileChipSize = (chipSize: ChipSize) => {
  if (chipSize === ChipSize.DEFAULT) return ChipSize.SMALL
  return ChipSize.EXTRA_SMALL
}

interface ChipProps {
  isSelected: boolean
  variant?: ChipType
  size?: ChipSize
  mobileSize?: ChipSize
  isFullWidth?: boolean
  hasLineBreaking?: boolean
  isTextWrap?: boolean
  style?: CSSProperties
  onClick?: () => void
  description?: string
  textAlign?: 'left' | 'center' | 'right'
}

interface CheckboxChipProps extends ChipProps {
  isFilledCheckbox?: boolean // TODO: 체크박스 스타일에 관해서 현재 코드가 복잡한 상태라 일관성 있게 리팩토링하기 (checkbox 타입을 따로 만들기?)
}

export const Chip: React.FCC<ChipProps> = ({
  children,
  isSelected,
  onClick,
  variant,
  size,
  mobileSize,
  isFullWidth,
  style,
  description,
}) => (
  <ChipContainer
    variant={variant}
    isSelected={isSelected}
    onClick={onClick}
    size={size}
    mobileSize={mobileSize}
    isFullWidth={isFullWidth}
    style={style}
  >
    <ChipLabel
      variant={variant}
      size={size}
      mobileSize={mobileSize}
      isSelected={isSelected}
    >
      {children}
    </ChipLabel>
    {description && (
      <Tippy
        content={
          <Balloon variant={BalloonType.LOW_BLACK}>{description}</Balloon>
        }
      >
        {/* Tippy 사용을 위해 Icon을 감싸야 함. */}

        <div>
          <TbInfoCircle size={15} />
        </div>
      </Tippy>
    )}
  </ChipContainer>
)

export const CheckboxChip: React.FCC<CheckboxChipProps> = ({
  children,
  isSelected,
  isFullWidth,
  isTextWrap,
  hasLineBreaking,
  isFilledCheckbox,
  variant,
  size,
  mobileSize,
  style,
  textAlign,
  onClick,
}) => (
  <ChipContainer
    style={{ gap: 8, ...style }}
    variant={variant}
    isSelected={isSelected}
    isFullWidth={isFullWidth}
    onClick={onClick}
    size={size}
    mobileSize={mobileSize}
  >
    <CheckboxIconWrapper
      isSelected={isSelected}
      variant={variant}
      size={size}
      isFilledCheckbox={isFilledCheckbox}
    >
      <FiCheck
        size={
          size === ChipSize.EXTRA_SMALL || size === ChipSize.XX_SMALL ? 16 : 20
        }
        color={
          isSelected
            ? isFilledCheckbox
              ? theme.color.white1
              : variant === ChipType.FILLED_PRIMARY
              ? theme.color.primary
              : theme.color.black4
            : theme.color.gray3
        }
      />
    </CheckboxIconWrapper>
    <ChipLabel
      variant={variant}
      isSelected={isSelected}
      size={size}
      mobileSize={mobileSize}
      hasLineBreaking={hasLineBreaking}
      isTextWrap={isTextWrap}
      textAlign={textAlign}
    >
      {children}
    </ChipLabel>
  </ChipContainer>
)

export const DeletableChip: React.FCC<ChipProps> = ({
  children,
  isSelected,
  isFullWidth,
  variant,
  size,
  mobileSize,
  onClick,
}) => (
  <ChipContainer
    style={{
      gap: size === ChipSize.EXTRA_SMALL || size === ChipSize.XX_SMALL ? 4 : 8,
    }}
    variant={variant}
    isSelected={isSelected}
    isFullWidth={isFullWidth}
    onClick={onClick}
    size={size}
    mobileSize={mobileSize}
  >
    <ChipLabel
      variant={variant}
      isSelected={isSelected}
      size={size}
      mobileSize={mobileSize}
    >
      {children}
    </ChipLabel>
    <AiOutlineClose
      size={
        size === ChipSize.EXTRA_SMALL || size === ChipSize.XX_SMALL ? 12 : 16
      }
    />
  </ChipContainer>
)

export const RadioChip: React.FCC<{
  isSelected: boolean
  isFullWidth?: boolean
  size?: ChipSize
  mobileSize?: ChipSize
  onClick?: () => void
}> = ({ children, isSelected, isFullWidth, size, mobileSize, onClick }) => (
  <RadioChipContainer
    isSelected={isSelected}
    isFullWidth={isFullWidth}
    onClick={onClick}
    size={size}
    mobileSize={mobileSize}
  >
    <RadioIcon
      isSelected={isSelected}
      size={size === ChipSize.DEFAULT ? 18 : 14}
    />
    <RadioLabel isSelected={isSelected} size={size} mobileSize={mobileSize}>
      {children}
    </RadioLabel>
  </RadioChipContainer>
)

const ChipContainer = styled(Row)<{
  isSelected: boolean
  variant?: ChipType
  size?: ChipSize
  mobileSize?: ChipSize
  isFullWidth?: boolean
}>`
  flex: none;
  border-radius: ${(p) =>
    ChipSizeMapping[p.size || ChipSize.DEFAULT].borderRadius};
  max-width: 100%;
  width: ${(p) => (p.isFullWidth ? '100%' : 'fit-content')};
  padding: ${(p) => ChipSizeMapping[p.size || ChipSize.DEFAULT].padding};
  border: 1px solid
    ${(p) =>
      p.isSelected
        ? ChipTypeMapping[p.variant || ChipType.FILLED].border
        : theme.color.line};
  svg {
    color: ${(p) =>
      p.isSelected
        ? ChipTypeMapping[p.variant || ChipType.FILLED].color
        : p.theme.color.black4};
  }
  background-color: ${(p) =>
    p.isSelected
      ? ChipTypeMapping[p.variant || ChipType.FILLED].background
      : p.theme.color.white1};
  :hover {
    background: ${(p) =>
      p.isSelected
        ? ChipTypeMapping[p.variant || ChipType.FILLED].hover
        : `linear-gradient(#E0E0DB29, #E0E0DB29), ${p.theme.color.white1}`};
  }
  :active {
    background: ${(p) =>
      p.isSelected
        ? ChipTypeMapping[p.variant || ChipType.FILLED].active
        : `linear-gradient(#E0E0DB42, #E0E0DB42), ${p.theme.color.white1}`};
  }
  cursor: pointer;
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: ${(p) => ChipSizeMapping[p.mobileSize || ChipSize.SMALL].padding};
  }
`

const ChipLabel = styled(H5Text)<{
  isSelected: boolean
  variant?: ChipType
  size?: ChipSize
  mobileSize?: ChipSize
  hasLineBreaking?: boolean
  isTextWrap?: boolean
  textAlign?: 'left' | 'center' | 'right'
}>`
  flex: 1;
  ${(p) => !p.hasLineBreaking && textEllipsis}
  text-align: ${(p) => p.textAlign || 'center'};
  line-height: 1.5;
  color: ${(p) =>
    p.isSelected
      ? ChipTypeMapping[p.variant || ChipType.FILLED].color
      : p.theme.color.black4};
  font-size: ${(p) => ChipSizeMapping[p.size || ChipSize.DEFAULT].fontSize};
  user-select: none;
  ${(p) => p.isTextWrap && `white-space: normal; text-align: start;`}
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) =>
      ChipSizeMapping[
        p.mobileSize || mapMobileChipSize(p.size || ChipSize.DEFAULT)
      ].fontSize};
  }
`

const CheckboxIconWrapper = styled(Row)<{
  isSelected: boolean
  variant?: ChipType
  size?: ChipSize
  isFilledCheckbox?: boolean
}>`
  flex: none;
  width: ${(p) =>
    p.size === ChipSize.EXTRA_SMALL || p.size === ChipSize.XX_SMALL
      ? 14
      : 18}px;
  height: ${(p) =>
    p.size === ChipSize.EXTRA_SMALL || p.size === ChipSize.XX_SMALL
      ? 14
      : 18}px;
  border-radius: 4px;
  border: 1px solid
    ${(p) =>
      p.isSelected
        ? ChipTypeMapping[p.variant || ChipType.FILLED][
            p.isFilledCheckbox ? 'border' : 'color' // 채워진 체크박스일 경우 테마 컬러는 테두리 컬러
          ]
        : p.theme.color.gray3};
  background-color: ${(p) =>
    p.isSelected && p.isFilledCheckbox
      ? ChipTypeMapping[p.variant || ChipType.FILLED].border // 채워진 체크박스일 경우 테마 컬러는 테두리 컬러
      : p.theme.color.white1};
`

const RadioChipContainer = styled(Row)<{
  isSelected: boolean
  isFullWidth?: boolean
  size?: ChipSize
  mobileSize?: ChipSize
}>`
  gap: 8px;
  width: ${(p) => (p.isFullWidth ? '100%' : 'fit-content')};
  padding: ${(p) => ChipSizeMapping[p.size || ChipSize.DEFAULT].padding};
  border: 1px solid
    ${(p) => (p.isSelected ? p.theme.color.primary6 : p.theme.color.gray3)};
  border-radius: 12px;
  background-color: ${(p) =>
    p.isSelected ? p.theme.color.primary6 : p.theme.color.white1};
  cursor: pointer;

  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: ${(p) => ChipSizeMapping[p.mobileSize || ChipSize.SMALL].padding};
    border-radius: 16px;
  }
`

const RadioLabel = styled(H5Text)<{
  isSelected: boolean
  size?: ChipSize
  mobileSize?: ChipSize
}>`
  flex: 1;
  text-align: center;
  color: ${(p) =>
    p.isSelected ? p.theme.color.primary : p.theme.color.black4};
  line-height: 1.5;
  font-size: ${(p) => ChipSizeMapping[p.size || ChipSize.DEFAULT].fontSize};
  @media ${(p) => p.theme.deviceSize.mobile} {
    font-size: ${(p) =>
      ChipSizeMapping[
        p.mobileSize || mapMobileChipSize(p.size || ChipSize.DEFAULT)
      ].fontSize};
  }
`

export const ChipWrapper = styled(Row)`
  flex-wrap: wrap;
  gap: 8px;
`
